import { html, css, unsafeCSS, LitElement } from 'lit';
import moment from 'moment';
import styles from './calendar.scss?inline';
import { isTabletMaxWidth } from '@/helpers';
import { range } from 'lit/directives/range.js';
import { map } from 'lit/directives/map.js';
import { ref, createRef } from 'lit/directives/ref.js';

export default class SotbitCalendar extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        isOpen: { type: Boolean, attribute: 'is-open', reflect: true },
        autoClose: { type: String, attribute: 'auto-close', reflect: true },
        numberOfMonths: {
            type: Number,
            attribute: 'number-of-months',
            reflect: true,
        },
        firstDay: { type: Number, attribute: 'first-day', reflect: true },
        weekdayStyle: {
            type: String,
            attribute: 'weekday-style',
            reflect: true,
        },
        format: { type: String, reflect: true },
        lang: { type: String, reflect: true },
        startDate: { type: String, attribute: 'start-date', reflect: true },
        endDate: { type: String, attribute: 'end-date', reflect: true },
        minDate: { type: String, attribute: 'min-date', reflect: true },
        maxDate: { type: String, attribute: 'max-date', reflect: true },
        disableWeekends: {
            type: Boolean,
            attribute: 'disable-weekends',
            reflect: true,
        },
        yearsMin: { type: Number, attribute: 'years-min', reflect: true },
        yearsMax: { type: Number, attribute: 'years-max', reflect: true },
        currentYear: { type: Number, state: true },
    };

    constructor() {
        super();

        this.isOpen = false;
        this.autoClose = 'off';
        this.numberOfMonths = 1;
        this.firstDay = 1;
        this.weekdayStyle = 'short';
        this.format = 'DD.MM.YYYY';
        this.lang = 'auto';
        this.startDate = null;
        this.endDate = null;
        this.minDate = null;
        this.maxDate = null;
        this.disableWeekends = false;
        this.yearsMin = 1900;
        this.yearsMax = null;
        this.calendar = [moment().set('date', 1)];
        this._dateCkecks = [
            (date) => {
                return !this.minDate || date.isAfter(this.minDate, 'day');
            },
            (date) => {
                return !this.maxDate || date.isBefore(this.maxDate, 'day');
            },
            (date) => {
                return !this.disableWeekends || (date.isoWeekday() !== 6 && date.isoWeekday() !== 7);
            },
        ];

        this.currentYear = this.calendar[0].get('year');
        this.monthSelectRef = createRef();
        this.yearSelectRef = createRef();
    }

    get input() {
        return this._input;
    }

    set input(input) {
        this.input?.removeEventListener('input', this._handleInputInput);
        this.input?.removeEventListener('click', this._handleInputClick);
        this._input = input;
        this.input?.addEventListener('input', this._handleInputInput);
        this.input?.addEventListener('click', this._handleInputClick);
    }

    getFormat() {
        return this.format;
    }

    pushDateCheck(callback) {
        this._dateCkecks.push(callback);
    }

    connectedCallback() {
        super.connectedCallback();
        this._processConfig();
        this.syncFields();
        document.addEventListener('click', this._handleOutsideClick);

        this.input?.removeEventListener('input', this._handleInputInput);
        this.input?.addEventListener('input', this._handleInputInput);
    }

    disconnectedCallback() {
        document.removeEventListener('click', this._handleOutsideClick);
        this.input?.removeEventListener('input', this._handleInputInput);
    }

    _handleInputClick = () => {
        if (this.disabled || this.input?.disabled) {
            return;
        }

        this.show();
    };

    _handleInputInput = () => {
        this.goToDate(this.input.value);

        this.syncFields();

        if (!this.isShowing) {
            this.show();
        }
    };

    _handleOutsideClick = (event) => {
        if (this.isOpen && !event.composedPath().includes(this)) {
            const yearModal = this.yearSelectRef?.value?.getModal();
            const monthModal = this.monthSelectRef?.value?.getModal();

            if (!event.composedPath().includes(yearModal) && !event.composedPath().includes(monthModal)) {
                this.hide();
            }
        }
    };

    _processConfig() {
        const minDateMoment = moment(this.minDate, this.format);

        this.minDate = this.minDate && minDateMoment.isValid() ? minDateMoment : null;

        const maxDateMoment = moment(this.maxDate, this.format);
        this.maxDate = this.maxDate && maxDateMoment.isValid() ? maxDateMoment : null;

        if (this.lang === 'auto') {
            const browserLang = navigator.language || navigator.userLanguage;
            this.lang = browserLang || 'en-US';
        }

        if (isTabletMaxWidth() && this.numberOfMonths > 1) {
            this.numberOfMonths = 1;
        }
    }

    _handleDayClick(dayElement) {
        const dayTime = parseInt(dayElement.getAttribute('data-time'));
        const day = moment(dayTime);

        this.setDate(day);
        if (this.autoclose !== 'off') {
            setTimeout(() => this.hide(), 100);
        }

        this.requestUpdate();
    }

    setStartDate(date) {
        const dateMoment = moment(date, this.format);
        if (!dateMoment.isValid()) {
            this.startDate = null;
            if (this.input) {
                this.input.value = '';
            }

            return;
        }

        this.startDate = dateMoment;
        this._updateFieldValues();
    }

    setEndDate(date) {
        const dateMoment = moment(date, this.format);
        if (!dateMoment.isValid()) {
            this.endDate = null;
            this._updateFieldValues();
            return;
        }

        this.endDate = dateMoment;
        this._updateFieldValues();
    }

    setDate(date) {
        this.setStartDate(date);

        this.dispatchEvent(
            new CustomEvent('setdate', {
                bubbles: true,
                composed: true,
                detail: {
                    value: date,
                    formattedValue: date.format(this.format),
                },
            })
        );

        this.input?.calculatePlaceholderShow();
        this.input?.calculateSmallLabel();
        this.requestUpdate();
    }

    _updateFieldValues() {
        if (!this.input) {
            return;
        }

        this.input.value = this.startDate ? this.startDate.format(this.format) : '';
    }

    syncFields() {
        if (!this.input) {
            return;
        }

        if (moment(this.input.value, this.format).isValid()) {
            this.startDate = moment(this.input.value, this.format);
        }
    }

    getStartDate() {
        return this.startDate ? this.startDate.clone() : null;
    }

    getEndDate() {
        return this.endDate ? this.endDate.clone() : null;
    }

    show() {
        this.isOpen = true;
        this.calendar = [moment(this.startDate || this.minDate || new Date()).set('date', 1)];

        this.dispatchEvent(
            new CustomEvent('open', {
                bubbles: true,
                composed: true,
            })
        );
    }

    hide() {
        this.isOpen = false;

        this.dispatchEvent(
            new CustomEvent('close', {
                bubbles: true,
                composed: true,
            })
        );
    }

    prevMonth() {
        this.calendar[0] = moment(this.calendar[0]).subtract(this.numberOfMonths, 'month');
        this.requestUpdate();
    }

    nextMonth() {
        this.calendar[0] = moment(this.calendar[0]).add(this.numberOfMonths, 'month');
        this.requestUpdate();
    }

    goToDate(date) {
        const dateMoment = moment(date, this.format);
        if (dateMoment.isValid()) {
            this.calendar = [dateMoment.set('date', 1)];
            this.requestUpdate();
        }
    }

    goToMonth(month) {
        if (isNaN(month)) {
            return;
        }

        this.calendar[0].set('month', month);
        this.requestUpdate();
    }

    goToYear(year) {
        if (isNaN(year)) {
            return;
        }

        this.calendar[0].set('year', year);
        this.requestUpdate();
    }

    _renderToolbar() {
        return html` <div class="lightpick__toolbar">
            <sotbit-icon icon="chevron-left" variant="secondary" pointer @mousedown="${this.prevMonth}"></sotbit-icon>
            <sotbit-icon icon="chevron-right" variant="secondary" pointer @mousedown="${this.nextMonth}"></sotbit-icon>
            ${this.autoClose !== 'off'
                ? html` <sotbit-icon icon="close" variant="secondary" pointer @mousedown="${this.hide}"></sotbit-icon> `
                : ''}
        </div>`;
    }

    _renderMonths() {
        const months = [];
        let currentDate = moment(this.calendar[0]);

        for (let i = 0; i < this.numberOfMonths; i++) {
            months.push(this._renderMonth(currentDate));
            currentDate = moment(currentDate).add(1, 'month');
        }

        return months;
    }

    _renderMonth(monthDate) {
        return html`
            <section class="lightpick__month">
                <div class="lightpick__month-title-bar">
                    <div class="lightpick__month-title">
                        ${this._renderMonthSelect(monthDate)} ${this._renderYearSelect(monthDate)}
                    </div>
                    ${this.numberOfMonths === 1 ? this._renderToolbar() : ''}
                </div>
                <div class="lightpick__days-of-the-week">${this._renderWeekDays()}</div>
                <div class="lightpick__days">${this._renderDays(monthDate)}</div>
            </section>
        `;
    }

    _renderWeekDays() {
        const days = [];
        for (let i = this.firstDay + 4; i < 7 + this.firstDay + 4; i++) {
            const dayName = this._getWeekdayName(i, this.weekdayStyle);
            const fullDayName = this._getWeekdayName(i, 'long');
            days.push(html` <div class="lightpick__day-of-the-week" title="${fullDayName}">${dayName}</div> `);
        }
        return days;
    }

    _getWeekdayName(day, style) {
        return new Date(1970, 0, day, 12).toLocaleString(this.lang, {
            weekday: style,
        });
    }

    _renderDays(monthDate) {
        const days = [];
        const firstDay = moment(monthDate).startOf('month');
        const lastDay = moment(monthDate).endOf('month');

        if (firstDay.isoWeekday() !== this.firstDay) {
            const prevMonth = moment(firstDay).subtract(1, 'month');
            const prevMonthEnd = moment(prevMonth).endOf('month');

            let prevDays = firstDay.isoWeekday() - this.firstDay;
            if (prevDays < 0) prevDays += 7;

            for (let i = prevDays - 1; i >= 0; i--) {
                const day = moment(prevMonthEnd).subtract(i, 'days');
                days.push(this._renderDay(day, false, ['is-previous-month']));
            }
        }

        let current = moment(firstDay);
        while (current.isSameOrBefore(lastDay)) {
            days.push(this._renderDay(current, false));
            current = moment(current).add(1, 'day');
        }

        const nextMonth = moment(lastDay).add(1, 'month');
        const daysNeeded = 35 - days.length; // 6 rows * 7 days
        for (let i = 1; i <= daysNeeded; i++) {
            days.push(this._renderDay(moment(nextMonth).set('date', i), false, ['is-next-month']));
        }

        return days;
    }

    _renderDay(date, isDummy, extraClasses = []) {
        if (isDummy) {
            return html` <sotbit-button-pagination disabled class="lightpick__day"></sotbit-button-pagination>`;
        }

        const isToday = date.isSame(new Date(), 'day');
        const isStartDate = this.startDate && date.isSame(this.startDate, 'day');
        const isEndDate = this.endDate && date.isSame(this.endDate, 'day');
        const isInRange = this.startDate && this.endDate && date.isBetween(this.startDate, this.endDate, 'day', '[]');

        const isDisabled = this._isDayDisabled(date);
        const classes = [
            'lightpick__day',
            isDisabled ? '' : 'is-available',
            isStartDate ? 'is-start-date' : '',
            isEndDate ? 'is-end-date' : '',
            isInRange ? 'is-in-range' : '',
            isDisabled ? 'is-disabled' : '',
            ...extraClasses,
        ]
            .filter(Boolean)
            .join(' ');

        return html`
            <sotbit-button-pagination
                class="${classes}"
                data-time="${date.valueOf()}"
                ?disabled="${isDisabled}"
                ?selected="${isStartDate}"
                ?active="${isToday}"
                @mousedown="${(e) => !isDisabled && this._handleDayClick(e.currentTarget)}"
            >
                <span slot="content">${date.date()}</span>
            </sotbit-button-pagination>
        `;
    }

    _isDayDisabled(date) {
        for (const callback of this._dateCkecks) {
            if (!callback(date)) {
                return true;
            }
        }

        return false;
    }

    _renderMonthSelect(date) {
        return html` <sotbit-dropdown
            @change="${(e) => {
                this.goToMonth(e?.detail?.value);
            }}"
            max-height
            is-select
            ${ref(this.monthSelectRef)}
            class="lightpick__select lightpick__select-months"
        >
            ${map(range(12), (i) => {
                const monthDate = moment(date).set('month', i);
                const monthName = ((str = monthDate.toDate().toLocaleString(this.lang, { month: 'short' })) =>
                    `${str.charAt(0).toUpperCase()}${str.slice(1)}`)();
                const selected = i === date.month();
                return html` ${selected
                        ? html` <sotbit-button-dropdown slot="anchor" light background>
                              <span slot="content">${monthName}</span>
                              <sotbit-icon no-padding size="m" slot="icon-right" icon="dropdown"></sotbit-icon>
                          </sotbit-button-dropdown>`
                        : ''}
                    <sotbit-dropdown-item ?active="${selected}" size="s" value="${i}" display-value="${monthName}">
                        <span slot="value">${monthName}</span>
                    </sotbit-dropdown-item>`;
            })}
        </sotbit-dropdown>`;
    }

    _renderYearSelect(date) {
        const years = { min: this.yearsMin, max: this.yearsMax };
        const minYear = (years.min || 1900) - 1;
        const maxYear = (years.max || moment().year()) + 1;
        const availableYear = date.year() <= maxYear && date.year() >= minYear;
        this.currentYear = date.year();

        return html` <sotbit-dropdown
            @change="${(e) => {
                this.goToYear(e?.detail?.value);
            }}"
            max-height
            is-select
            ${ref(this.yearSelectRef)}
            class="lightpick__select lightpick__select-months"
        >
            ${!availableYear
                ? html` <sotbit-button-dropdown slot="anchor" light background>
                      <span slot="content">${this.currentYear}</span>
                      <sotbit-icon no-padding size="m" slot="icon-right" icon="dropdown"></sotbit-icon>
                  </sotbit-button-dropdown>`
                : ''}
            ${map(range(maxYear, minYear, -1), (year) => {
                const selected = year === this.currentYear;

                return html` ${selected && availableYear
                        ? html` <sotbit-button-dropdown slot="anchor" light background>
                              <span slot="content">${year}</span>
                              <sotbit-icon no-padding size="m" slot="icon-right" icon="dropdown"></sotbit-icon>
                          </sotbit-button-dropdown>`
                        : ''}
                    <sotbit-dropdown-item ?active="${selected}" size="s" value="${year}" display-value="${year}">
                        <span slot="value">${year}</span>
                    </sotbit-dropdown-item>`;
            })}
        </sotbit-dropdown>`;
    }

    _handleSlotChange(e) {
        const slots = e.target.assignedElements();
        this.input = slots[0];
    }

    render() {
        return html`
            <slot name="input" @slotchange="${this._handleSlotChange}"></slot>
            ${this.isOpen
                ? html` <div class="lightpick">
                      <div class="lightpick__inner">
                          ${this.numberOfMonths > 1 ? this._renderToolbar() : ''}
                          <div class="lightpick__months">${this._renderMonths()}</div>
                      </div>
                  </div>`
                : html` <div class="lightpick is-hidden"></div>`}
        `;
    }
}
